import React from "react";
import Loader from "../../../components/Loader";
import localStyles from "../styles.module.scss";

export interface AdminTableColumn {
    label: string;
    key: string;
    sortable?: boolean;
    render?: (value: any, row: any) => React.ReactNode;
}

interface AdminTableProps {
    columns: AdminTableColumn[];
    data: any[];
    total: number;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
    loading?: boolean;
    onRowClick?: (row: any) => void;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
    onSortChange?: (sortBy: string, sortOrder: "asc" | "desc") => void;
}

const AdminTable = ({
    columns,
    data,
    total,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    loading,
    onRowClick,
    sortBy,
    sortOrder = "desc",
    onSortChange,
}: AdminTableProps) => {
    const totalPages = Math.ceil(total / pageSize);

    const handleSort = (column: AdminTableColumn) => {
        if (!column.sortable || !onSortChange) return;

        if (sortBy === column.key) {
            onSortChange(column.key, sortOrder === "asc" ? "desc" : "asc");
        } else {
            onSortChange(column.key, "desc");
        }
    };

    const renderSortIcon = (column: AdminTableColumn) => {
        if (!column.sortable) return null;

        if (sortBy !== column.key) {
            return <span className={localStyles.sortIcon}>⇅</span>;
        }

        return (
            <span className={`${localStyles.sortIcon} ${localStyles.sortIconActive}`}>
                {sortOrder === "asc" ? "↑" : "↓"}
            </span>
        );
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const items: React.ReactNode[] = [];
        const maxVisible = 5;
        let start = Math.max(1, page - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible - 1);

        if (end - start < maxVisible - 1) {
            start = Math.max(1, end - maxVisible + 1);
        }

        items.push(
            <button
                key="first"
                className={localStyles.pageBtn}
                onClick={() => onPageChange(1)}
                disabled={page === 1}
            >
                &laquo;
            </button>,
            <button
                key="prev"
                className={localStyles.pageBtn}
                onClick={() => onPageChange(page - 1)}
                disabled={page === 1}
            >
                &lsaquo;
            </button>
        );

        if (start > 1) {
            items.push(
                <span key="start-ellipsis" className={localStyles.ellipsis}>...</span>
            );
        }

        for (let i = start; i <= end; i++) {
            items.push(
                <button
                    key={i}
                    className={`${localStyles.pageBtn} ${i === page ? localStyles.pageBtnActive : ""}`}
                    onClick={() => onPageChange(i)}
                >
                    {i}
                </button>
            );
        }

        if (end < totalPages) {
            items.push(
                <span key="end-ellipsis" className={localStyles.ellipsis}>...</span>
            );
        }

        items.push(
            <button
                key="next"
                className={localStyles.pageBtn}
                onClick={() => onPageChange(page + 1)}
                disabled={page === totalPages}
            >
                &rsaquo;
            </button>,
            <button
                key="last"
                className={localStyles.pageBtn}
                onClick={() => onPageChange(totalPages)}
                disabled={page === totalPages}
            >
                &raquo;
            </button>
        );

        return <div className={localStyles.pagination}>{items}</div>;
    };

    if (loading) {
        return (
            <div className="d-flex" style={{justifyContent: "center", padding: "40px 0"}}>
                <Loader/>
            </div>
        );
    }

    return (
        <div>
            <div style={{overflowX: "auto"}}>
                <table className={`${localStyles.table} ${onRowClick ? localStyles.tableClickable : ""}`}>
                    <thead>
                        <tr>
                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    onClick={() => handleSort(col)}
                                    className={col.sortable ? localStyles.sortableHeader : ""}
                                >
                                    {col.label}
                                    {renderSortIcon(col)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length} className={localStyles.noData}>
                                    No data
                                </td>
                            </tr>
                        ) : (
                            data.map((row, idx) => (
                                <tr
                                    key={row.id ?? idx}
                                    onClick={() => onRowClick?.(row)}
                                >
                                    {columns.map((col) => (
                                        <td key={col.key}>
                                            {col.render ? col.render(row[col.key], row) : row[col.key]}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className={localStyles.paginationContainer}>
                <div className={localStyles.paginationInfo}>
                    <span>Total: {total}</span>
                    {onPageSizeChange && (
                        <select
                            className={localStyles.pageSizeSelect}
                            value={pageSize}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                        >
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    )}
                </div>
                {renderPagination()}
            </div>
        </div>
    );
};

export default AdminTable;
